<template>
  <div ref="video" />
</template>

<script setup lang="ts">
import Player from "@vimeo/player";
const api = useApi();

const props = defineProps<{
    videoUrl: string,
    autoPlay?: boolean
    markAsViewed?: boolean
}>();

interface MarkedAsViewedResponse {
  message: string;
  tutorial_id: string;
}

const { $bus } = useNuxtApp();

let player: Player | null = null;
const video = ref<HTMLDivElement | null>(null);
const videoIsReady = ref<boolean>(false);
const emit = defineEmits(["setVideoClass"]);
const profileStore = useProfileStore();

const options = computed<Record<string, any>>(() => ({
  id: props.videoUrl,
  autoplay: props.autoPlay || false,
  responsive: true
}));

async function markVideoAsViewed (url: string) {
  const { error } = await api.call<MarkedAsViewedResponse>(api.commonUrls.auth.MARK_AS_ACCESSED(), {
    method: "POST",
    body: {
      video_url: url
    }
  });
  if (error.value) {
    useHandleError(error.value);
  } else {
    profileStore.updateIsAccessed(null, url);
  }
}

async function pauseVideo () {
  if (player) {
    if (!(await player.getPaused())) {
      player.pause();
    }
  }
}

function handleSize (videoHeight: number, videoWidth: number) {
  emit("setVideoClass", { height: videoHeight, width: videoWidth });
}

onMounted(async () => {
  if (!video.value) {
    return;
  }
  player = new Player(unref(video) as HTMLDivElement, unref(options));
  handleSize(await player.getVideoHeight(), await player.getVideoWidth());

  await player.ready();
  videoIsReady.value = true;

  player.on("play", async () => {
    if (props.markAsViewed) {
      await markVideoAsViewed(props.videoUrl);
      $bus.emit($bus.EVENT_TYPES.VIDEO_WATCHED, { url: props.videoUrl, isWatched: true });
    }
  });

  $bus.on($bus.EVENT_TYPES.ALL_VIDEOS_PAUSE, pauseVideo);
});

onBeforeUnmount(() => {
  if (player) {
    player.unload();
  }
  $bus.off($bus.EVENT_TYPES.ALL_VIDEOS_PAUSE, pauseVideo);
});
</script>
